import React from 'react';
import {
  Leaf,
  Wheat,
  WheatOff,
  Milk,
  Fish,
  Egg,
  Shell,
  Nut,
  Bean,
  Carrot,
  Droplet,
  Sprout,
  ChefHat,
  Flame,
  Flower2,
  Waves,
  FlaskConical,
} from 'lucide-react';
import { HalalIcon } from '@/components/icons/HalalIcon';
import { KosherIcon } from '@/components/icons/KosherIcon';
import { VeganIcon } from '@/components/icons/VeganIcon';

type IconComponent = React.FC<{ className?: string }>;

// ============================================
// Dietary Claims — component references
// ============================================
const dietaryIconComponents: Record<string, IconComponent> = {
  'vegan': VeganIcon as IconComponent,
  'vegetarian': Leaf as IconComponent,
  'halal': HalalIcon as IconComponent,
  'kosher': KosherIcon as IconComponent,
  'gluten-free': WheatOff as IconComponent,
  'organic': Sprout as IconComponent,
  'pescatarian': Fish as IconComponent,
  'dairy-free': Milk as IconComponent,
};

// Extra classes per key (e.g. opacity for dairy-free)
const dietaryIconExtraClass: Record<string, string> = {
  'dairy-free': 'opacity-50',
};

// ============================================
// Allergen — component references
// ============================================
const allergenIconComponents: Record<string, IconComponent> = {
  // UK 14 allergen keys (stored in DB)
  'celery': Carrot as IconComponent,
  'cereals': Wheat as IconComponent,
  'crustaceans': Shell as IconComponent,
  'eggs': Egg as IconComponent,
  'fish': Fish as IconComponent,
  'lupin': Flower2 as IconComponent,
  'milk': Milk as IconComponent,
  'molluscs': Waves as IconComponent,
  'mustard': Flame as IconComponent,
  'tree-nuts': Nut as IconComponent,
  'peanuts': Sprout as IconComponent,
  'sesame': Leaf as IconComponent,
  'soya': Bean as IconComponent,
  'sulphites': FlaskConical as IconComponent,
  // Legacy/alternate keys also found in DB
  'gluten': Wheat as IconComponent,
  'dairy': Milk as IconComponent,
  'soy': Bean as IconComponent,
  'shellfish': Shell as IconComponent,
  'nuts': Nut as IconComponent,
};

// ============================================
// Legacy static maps (kept for back-compat)
// ============================================
export const dietaryIcons: Record<string, React.ReactNode> = {
  'vegan': <VeganIcon className="h-5 w-5" />,
  'vegetarian': <Leaf className="h-5 w-5" />,
  'halal': <HalalIcon className="h-5 w-5" />,
  'kosher': <KosherIcon className="h-5 w-5" />,
  'gluten-free': <WheatOff className="h-5 w-5" />,
  'organic': <Sprout className="h-5 w-5" />,
  'pescatarian': <Fish className="h-5 w-5" />,
  'dairy-free': <Milk className="h-5 w-5 opacity-50" />,
};

export const allergenIcons: Record<string, React.ReactNode> = {
  'celery': <Carrot className="h-5 w-5" />,
  'cereals': <Wheat className="h-5 w-5" />,
  'crustaceans': <Shell className="h-5 w-5" />,
  'eggs': <Egg className="h-5 w-5" />,
  'fish': <Fish className="h-5 w-5" />,
  'lupin': <Flower2 className="h-5 w-5" />,
  'milk': <Milk className="h-5 w-5" />,
  'molluscs': <Waves className="h-5 w-5" />,
  'mustard': <Flame className="h-5 w-5" />,
  'tree-nuts': <Nut className="h-5 w-5" />,
  'peanuts': <Sprout className="h-5 w-5" />,
  'sesame': <Leaf className="h-5 w-5" />,
  'soya': <Bean className="h-5 w-5" />,
  'sulphites': <FlaskConical className="h-5 w-5" />,
  'gluten': <Wheat className="h-5 w-5" />,
  'dairy': <Milk className="h-5 w-5" />,
  'soy': <Bean className="h-5 w-5" />,
  'shellfish': <Shell className="h-5 w-5" />,
  'nuts': <Nut className="h-5 w-5" />,
};

// ============================================
// Cuisine Icons (Flag Emojis)
// ============================================
export const cuisineIcons: Record<string, string> = {
  'italian': '🇮🇹',
  'chinese': '🇨🇳',
  'japanese': '🇯🇵',
  'mexican': '🇲🇽',
  'indian': '🇮🇳',
  'french': '🇫🇷',
  'thai': '🇹🇭',
  'greek': '🇬🇷',
  'american': '🇺🇸',
  'mediterranean': '🌊',
  'korean': '🇰🇷',
  'vietnamese': '🇻🇳',
  'spanish': '🇪🇸',
  'turkish': '🇹🇷',
  'middle_eastern': '🌍',
  'other': '🍽️',
};

// ============================================
// Helpers
// ============================================
export const getDietaryIcon = (iconKey: string | null, className = 'h-5 w-5'): React.ReactNode => {
  if (!iconKey) return <ChefHat className={className} />;
  const key = iconKey.toLowerCase();
  const IconComponent = dietaryIconComponents[key];
  if (!IconComponent) return <ChefHat className={className} />;
  const extra = dietaryIconExtraClass[key];
  return <IconComponent className={extra ? `${className} ${extra}` : className} />;
};

export const getAllergenIcon = (iconKey: string | null, className = 'h-5 w-5'): React.ReactNode => {
  if (!iconKey) return <Droplet className={className} />;
  const key = iconKey.toLowerCase();
  const IconComponent = allergenIconComponents[key];
  if (!IconComponent) return <Droplet className={className} />;
  return <IconComponent className={className} />;
};

export const getCuisineIcon = (cuisine: string | null): string => {
  if (!cuisine) return '🍽️';
  return cuisineIcons[cuisine.toLowerCase()] || '🍽️';
};
